import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/services/api';

export interface ConversaLeadApi {
  channel_id: string;
  is_ai_disabled: boolean | null;
  disabled_since: string | null;
  numero_real: string | null;
  lid: string | null;
}

export interface LeadApi {
  id: number;
  nome: string;
  email: string;
  telefone: string | null;
  empresa: string | null;
  cargo: string | null;
  status: string;
  fonte: string | null;
  ia_ativo: boolean;
  created_at: string;
  updated_at: string;
  responsavel_nome: string | null;
  // Paid-traffic / ad attribution fields
  is_trafego_pago: boolean;
  ad_source_app: string | null;
  ad_source_url: string | null;
  ad_source_id: string | null;
  ad_source_type: string | null;
  ad_ctwa_clid: string | null;
  ad_title: string | null;
  ad_body: string | null;
  ad_media_type: string | null;
  ad_thumbnail_url: string | null;
  ad_media_url: string | null;
  // Qualification & scoring fields (migration 012)
  lead_health_score?: number;
  lead_classification?: 'FRIO' | 'MORNO' | 'QUENTE' | 'PRIORIDADE';
  etapa_funil?: string;
  segmento?: string;
  faturamento_mensal_faixa?: string;
  cnpj_maturidade?: string;
  regularidade_fiscal?: string;
  experiencia_licitacoes?: string;
  decisor?: string;
  urgencia_meses?: string;
  potencial_conservador?: number;
  potencial_realista?: number;
  potencial_ambicioso?: number;
  calculadora_concluida?: boolean;
  agendamento_sugerido?: boolean;
  // Enriched WhatsApp conversation data
  conversa?: ConversaLeadApi | null;
}

interface LeadsResponse {
  success: boolean;
  data: {
    leads: LeadApi[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
      hasNextPage: boolean;
      hasPrevPage: boolean;
    };
  };
}

export function useLeads(params?: { search?: string; status?: string; page?: number; limit?: number }) {
  return useQuery({
    queryKey: ['leads', params],
    queryFn: async () => {
      const response = await api.get<LeadsResponse>('/leads', {
        params: {
          page: params?.page || 1,
          limit: params?.limit || 20,
          ...(params?.search ? { search: params.search } : {}),
          ...(params?.status ? { status: params.status } : {})
        }
      });
      return response.data;
    }
  });
}

export function useToggleIALead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, iaAtivo }: { id: number; iaAtivo: boolean }) => {
      const response = await api.patch<{ success: boolean; data: { lead: { id: number; nome: string; ia_ativo: boolean } } }>(
        `/leads/${id}/toggle-ia`,
        { ia_ativo: iaAtivo }
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
    }
  });
}
